import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "amountDue",
    "agreeToPostTerms",
    "startDate",
    "paymentDetails",
    "paymentMethodField",
    "submitButton",
    "cardElement",
    "alertBox",
    "billingInterval",
    "subtotal",
    "taxLabel",
    "taxAmount",
    "taxTotal",
    "taxNotice",
    "taxSummary",
    "promotionCode",
    "promotionMessage"
  ]

  static values = {
    currency: { type: String, default: "USD" },
    purchaseableId: Number,
    purchaseableType: String,
    taxPreviewUrl: String
  }

  connect() {
    this.cardComplete = false
    const locale = this.currencyValue === "CAD" ? "en-CA" : "en-US"
    this.moneyFormatter = new Intl.NumberFormat(locale, { style: "currency", currency: this.currencyValue })

    if (this.requiresStripe) {
      this.setupStripe()
    }

    this.ensureSubmitability()
    if (this.hasTaxPreviewUrlValue || this.element.querySelector("#listing_amount_cents, [data-amount-cents]")) this.reconcilePrice()
  }

  async setupStripe() {
    try {
      await this.loadStripeJs()
    } catch (error) {
      this.showError(error)
      return
    }

    // The controller may have been disconnected (e.g. Turbo navigation) while
    // Stripe.js was loading.
    if (!this.element.isConnected) return

    const stripeKey = this.paymentDetailsTarget.dataset.stripePublishableKey
    this.stripe = Stripe(stripeKey)
    const elements = this.stripe.elements()
    this.card = elements.create("card", { disableLink: true })
    this.card.mount(this.cardElementTarget)
    this.card.on("change", ({ complete, error }) => {
      this.cardComplete = complete
      if (error) {
        this.showError(error)
      } else {
        this.hideError()
      }
      this.ensureSubmitability()
    })

    this.ensureSubmitability()
  }

  // Loads Stripe.js on demand. The <script> tag is rendered outside of Turbo
  // Frames and may not have executed yet when the controller connects after a
  // Turbo navigation, so we ensure it is present before using `Stripe`.
  // Resolves immediately when Stripe is already loaded.
  loadStripeJs() {
    if (window.Stripe) return Promise.resolve()

    const src = "https://js.stripe.com/v3/"
    const existing = document.querySelector(`script[src="${src}"]`)

    return new Promise((resolve, reject) => {
      const script = existing || document.createElement("script")
      script.addEventListener("load", () => resolve(), { once: true })
      script.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")), { once: true })

      if (!existing) {
        script.src = src
        document.head.appendChild(script)
      }
    })
  }

  disconnect() {
    this.card?.destroy()
    this.taxPreviewRequest?.abort()
  }

  get requiresStripe() {
    return this.hasPaymentDetailsTarget && this.hasPaymentMethodFieldTarget
  }

  // Actions

  ensureSubmitability() {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = !this.isSubmittable() || !!this.element.dataset.submitted
  }

  reconcilePrice() {
    if (!this.hasAmountDueTarget) return

    let amountCents = 0

    const listingAmountEl = this.element.querySelector("#listing_amount_cents")
    if (listingAmountEl) {
      const parsed = parseInt(listingAmountEl.value, 10)
      if (!Number.isNaN(parsed) && parsed > 0) amountCents += parsed
    }

    this.element.querySelectorAll("[data-amount-cents]:checked").forEach((el) => {
      amountCents += parseInt(el.dataset.amountCents, 10)
    })
    this.element.querySelectorAll("select[data-contains-amount-cents]").forEach((select) => {
      const selectedAmount = parseInt(select.selectedOptions[0]?.dataset.amountCents, 10)
      if (!Number.isNaN(selectedAmount)) amountCents += selectedAmount
    })

    const formattedSubtotal = this.moneyFormatter.format(amountCents / 100)
    this.amountDueTarget.textContent = formattedSubtotal
    if (this.hasSubtotalTarget) this.subtotalTarget.textContent = formattedSubtotal
    if (this.hasTaxSummaryTarget) this.taxSummaryTarget.hidden = true
    if (this.hasTaxNoticeTarget) this.taxNoticeTarget.hidden = false
    this.hidePromotionMessage()

    this.refreshTaxPreview(amountCents)
  }

  handleSubmit(event) {
    if (!this.isSubmittable()) return

    if (this.requiresStripe) {
      if (this.paymentMethodFieldTarget.value || this.element.dataset.submitted) return

      event.preventDefault()
      this.stripe
        .createPaymentMethod({ type: "card", card: this.card })
        .then((result) => this.completeOrder(result))
        .catch((error) => this.showError(error))
        .finally(() => this.ensureSubmitability())
    } else {
      event.preventDefault()
      this.completeOrder()
    }
  }

  // Private

  async refreshTaxPreview(amountCents) {
    if (!this.hasTaxPreviewUrlValue || amountCents <= 0) return

    this.taxPreviewRequest?.abort()
    this.taxPreviewRequest = new AbortController()

    try {
      const response = await fetch(this.taxPreviewUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          purchaseable_id: this.hasPurchaseableIdValue ? this.purchaseableIdValue : null,
          purchaseable_type: this.purchaseableTypeValue,
          quantity: this.element.querySelector("[data-contains-amount-cents]")?.value,
          starts_featured: this.element.querySelector("[data-contains-amount-cents]:checked") !== null,
          promotion_code: this.hasPromotionCodeTarget ? this.promotionCodeTarget.value : null
        }),
        signal: this.taxPreviewRequest.signal
      })
      const preview = await response.json()
      if (!response.ok) throw new Error(preview.error || "Price preview request failed")

      this.applyPricePreview(preview)
      this.showPromotionApplied(preview)
    } catch (error) {
      if (error.name !== "AbortError") this.showPromotionError(error.message)
    }
  }

  applyPricePreview(eventOrPreview) {
    const preview = eventOrPreview.detail || eventOrPreview
    if (!this.hasAmountDueTarget) return

    this.amountDueTarget.textContent = this.moneyFormatter.format(preview.total_cents / 100)
    if (!this.hasTaxSummaryTarget) return

    this.subtotalTarget.textContent = this.moneyFormatter.format(preview.subtotal_cents / 100)
    this.taxLabelTarget.textContent = preview.tax_label
    this.taxAmountTarget.textContent = this.moneyFormatter.format(preview.tax_amount_cents / 100)
    this.taxTotalTarget.textContent = this.moneyFormatter.format(preview.total_cents / 100)
    this.taxSummaryTarget.hidden = false
    if (this.hasTaxNoticeTarget) this.taxNoticeTarget.hidden = true
  }

  showPromotionApplied(preview) {
    if (!preview.promotion_code || !this.hasPromotionMessageTarget) return

    this.promotionMessageTarget.textContent =
      `${preview.promotion_code} applied: -${this.moneyFormatter.format(preview.promotion_discount_cents / 100)}`
    this.promotionMessageTarget.classList.remove("text-red-600")
    this.promotionMessageTarget.classList.add("text-green-700")
    this.promotionMessageTarget.hidden = false
  }

  showPromotionError(message) {
    if (!this.hasPromotionMessageTarget) return

    this.promotionMessageTarget.textContent = message
    this.promotionMessageTarget.classList.remove("text-green-700")
    this.promotionMessageTarget.classList.add("text-red-600")
    this.promotionMessageTarget.hidden = false
  }

  hidePromotionMessage() {
    if (!this.hasPromotionMessageTarget) return

    this.promotionMessageTarget.hidden = true
    this.promotionMessageTarget.textContent = ""
  }

  isSubmittable() {
    const hasValidCard = !this.requiresStripe || this.cardComplete
    const hasAgreedToTerms = this.agreedToTerms()
    const hasStartDate = !this.hasStartDateTarget || !!this.startDateTarget.value
    const hasBillingInterval = this.billingIntervalSelected()

    return hasValidCard && hasAgreedToTerms && hasStartDate && hasBillingInterval
  }

  agreedToTerms() {
    if (!this.hasAgreeToPostTermsTarget) return true
    return this.agreeToPostTermsTarget.checked
  }

  billingIntervalSelected() {
    if (!this.hasBillingIntervalTarget) return true
    return this.billingIntervalTargets.some((el) => el.checked)
  }

  showError(error) {
    if (!this.requiresStripe) return

    this.cardComplete = false
    console.error(error)

    const message =
      error.status >= 500
        ? "We're having trouble processing your payment, try again later."
        : error.message || error

    this.alertBoxTarget.textContent = message
    this.alertBoxTarget.classList.remove("alert-info", "alert-success")
    this.alertBoxTarget.classList.add("alert-warning", "alert")
    this.ensureSubmitability()
  }

  hideError() {
    if (!this.requiresStripe) return

    if (this.alertBoxTarget.classList.contains("alert-warning")) {
      this.alertBoxTarget.textContent = ""
      this.alertBoxTarget.classList.remove("alert-warning", "alert")
    }
    this.ensureSubmitability()
  }

  completeOrder(result) {
    if (this.requiresStripe) {
      if (result?.error) throw result.error
      this.paymentMethodFieldTarget.value = result.paymentMethod.id
    }

    this.element.dataset.submitted = "true"
    this.ensureSubmitability()
    this.element.submit()
  }
}
